import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { trackFormStart, trackFormSubmit } from "@/lib/analytics";
import { ArrowRight, ArrowLeft, CheckCircle2, Download, Linkedin, Twitter, Share2 } from "lucide-react";

// ─── Types ────────────────────────────────────────────────────────────────────

interface Question {
  metric: string;
  metricLabel: string;
  accentColor: string;
  question: string;
  subtext: string;
  options: { label: string; value: number; description: string }[];
}

export interface AssessmentResult {
  metric: string;
  metricLabel: string;
  accentColor: string;
  score: number; // 0–3
  label: string;
  advice: string;
}

// ─── Questions (one per GEO metric) ──────────────────────────────────────────

const QUESTIONS: Question[] = [
  {
    metric: "discoverability",
    metricLabel: "Discoverability",
    accentColor: "#00D4AA",
    question: "How well is your content structured for AI retrieval?",
    subtext: "Discoverability measures whether your content enters the retrieval candidate pool when AI engines process relevant queries.",
    options: [
      { label: "Not structured", value: 0, description: "No schema markup, no structured headings, no dedicated FAQ content" },
      { label: "Partially structured", value: 1, description: "Some schema markup but inconsistent; headings present but not answer-optimised" },
      { label: "Mostly structured", value: 2, description: "Schema markup on key pages, FAQ sections, clear H2/H3 hierarchy" },
      { label: "Fully optimised", value: 3, description: "Comprehensive schema, speakable markup, entity-rich content, regular crawl verification" },
    ],
  },
  {
    metric: "mention_probability",
    metricLabel: "Mention Probability",
    accentColor: "#FF8C00",
    question: "How often does your brand appear when AI engines respond to queries in your category?",
    subtext: "Mention Probability measures how frequently an AI engine surfaces your brand name when it has retrieved your content.",
    options: [
      { label: "Rarely or never", value: 0, description: "We have not tested this and have no evidence of AI mentions" },
      { label: "Occasionally", value: 1, description: "We appear in some AI responses but inconsistently and mostly for branded queries" },
      { label: "Regularly", value: 2, description: "We appear in AI responses for several non-branded category queries" },
      { label: "Consistently", value: 3, description: "We systematically track mention rate across 20+ queries and optimise based on results" },
    ],
  },
  {
    metric: "citation_probability",
    metricLabel: "Citation Probability",
    accentColor: "#7C3AED",
    question: "When your brand is mentioned by an AI engine, does it cite your URL as the source?",
    subtext: "Citation Probability measures the proportion of AI mentions that include a clickable URL attribution back to your domain.",
    options: [
      { label: "No citations", value: 0, description: "Our brand may be mentioned but we have never seen a URL citation" },
      { label: "Rare citations", value: 1, description: "We occasionally appear as a cited source but it is unpredictable" },
      { label: "Moderate citations", value: 2, description: "We are cited on Perplexity or ChatGPT Search for some queries" },
      { label: "Strong citations", value: 3, description: "We track citation rate per query and have a programme to improve it" },
    ],
  },
  {
    metric: "narrative_accuracy",
    metricLabel: "Narrative Accuracy",
    accentColor: "#DC2626",
    question: "How accurate are the claims AI engines attribute to your brand?",
    subtext: "Narrative Accuracy measures whether the content attributed to you by AI engines is factually correct and commercially aligned.",
    options: [
      { label: "Unknown / not checked", value: 0, description: "We have never audited what AI engines say about us" },
      { label: "Some inaccuracies", value: 1, description: "We have found incorrect claims but have not systematically addressed them" },
      { label: "Mostly accurate", value: 2, description: "We periodically check AI responses and correct major inaccuracies via content updates" },
      { label: "Actively managed", value: 3, description: "We run regular accuracy audits and have a correction workflow for hallucinated claims" },
    ],
  },
  {
    metric: "recommendation_rate",
    metricLabel: "Recommendation Rate",
    accentColor: "#0891B2",
    question: "Do AI engines actively recommend your brand as a solution?",
    subtext: "Recommendation Rate measures how often an AI engine goes beyond mentioning your brand to actively suggesting it as the best option for a user's need.",
    options: [
      { label: "Not recommended", value: 0, description: "AI engines do not recommend us even when we are mentioned" },
      { label: "Occasionally recommended", value: 1, description: "We appear in some 'best of' or 'you might consider' responses" },
      { label: "Frequently recommended", value: 2, description: "We are recommended for specific use cases or buyer profiles" },
      { label: "Preferred recommendation", value: 3, description: "We are the top recommendation for our primary category queries across multiple engines" },
    ],
  },
  {
    metric: "commercial_influence",
    metricLabel: "Commercial Influence",
    accentColor: "#059669",
    question: "Can you connect AI visibility to qualified pipeline or revenue?",
    subtext: "Commercial Influence measures whether your GEO programme demonstrably affects business outcomes - leads, demos, or revenue attributable to AI-referred traffic.",
    options: [
      { label: "No measurement", value: 0, description: "We do not track where leads come from with AI channel granularity" },
      { label: "Anecdotal evidence", value: 1, description: "Prospects occasionally mention they found us via AI search but we cannot quantify it" },
      { label: "Basic attribution", value: 2, description: "We track AI-referred traffic in GA4 and can see conversion rates" },
      { label: "Full attribution", value: 3, description: "We use interrupted time-series or controlled measurement to isolate GEO's revenue contribution" },
    ],
  },
];

// ─── Score helpers ────────────────────────────────────────────────────────────

function getScoreLabel(score: number): { label: string; advice: string } {
  if (score === 0) return {
    label: "Not started",
    advice: "This metric needs foundational work before optimisation can begin.",
  };
  if (score === 1) return {
    label: "Early stage",
    advice: "You have some activity here but no systematic programme yet.",
  };
  if (score === 2) return {
    label: "Developing",
    advice: "Good progress - the next step is systematic tracking and iteration.",
  };
  return {
    label: "Advanced",
    advice: "You are ahead of most organisations on this metric. Focus on maintaining and scaling.",
  };
}

function getOverallBand(total: number): { band: string; description: string; color: string } {
  const max = QUESTIONS.length * 3; // 18
  const pct = total / max;
  if (pct < 0.25) return { band: "GEO Unready", description: "Your organisation has not yet built the foundations for a measurable GEO programme. Start with Discoverability and Narrative Accuracy.", color: "#DC2626" };
  if (pct < 0.5) return { band: "GEO Emerging", description: "You have made a start but significant gaps remain across the six metrics. Prioritise the lowest-scoring areas first.", color: "#FF8C00" };
  if (pct < 0.75) return { band: "GEO Developing", description: "You have a working GEO programme with measurable activity. Focus on closing the gaps in Citation Probability and Commercial Influence.", color: "#0891B2" };
  return { band: "GEO Advanced", description: "You are running a sophisticated, evidence-led GEO programme. The priority is cross-engine coverage and Commercial Influence measurement.", color: "#059669" };
}

// ─── Component ────────────────────────────────────────────────────────────────

export interface GEOAssessmentPayload {
  results: AssessmentResult[];
  totalScore: number;
  band: string;
  bandColor: string;
  bandDescription: string;
}

interface GEOReadinessAssessmentProps {
  onComplete?: (payload: GEOAssessmentPayload) => void;
}

export default function GEOReadinessAssessment({ onComplete }: GEOReadinessAssessmentProps = {}) {
  const [step, setStep] = useState(0); // 0 = intro, 1–6 = questions, 7 = contact, 8 = results
  const [answers, setAnswers] = useState<number[]>(new Array(QUESTIONS.length).fill(-1));
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formStarted, setFormStarted] = useState(false);
  const [error, setError] = useState("");

  const submitLead = trpc.aeoGuide.submitLead.useMutation();

  const totalSteps = QUESTIONS.length + 2; // intro + 6 questions + contact = 8 states; results = 9th

  // Build results from answers
  const results: AssessmentResult[] = QUESTIONS.map((q, i) => {
    const score = answers[i] >= 0 ? answers[i] : 0;
    const { label, advice } = getScoreLabel(score);
    return { metric: q.metric, metricLabel: q.metricLabel, accentColor: q.accentColor, score, label, advice };
  });
  const totalScore = results.reduce((sum, r) => sum + r.score, 0);
  const overall = getOverallBand(totalScore);

  function handleStart() {
    if (!formStarted) {
      trackFormStart({ form_name: "GEO Readiness Assessment", form_location: "blog_geo_methodology" });
      setFormStarted(true);
    }
    setStep(1);
  }

  function handleAnswer(questionIndex: number, value: number) {
    const newAnswers = [...answers];
    newAnswers[questionIndex] = value;
    setAnswers(newAnswers);
    // Auto-advance after short delay
    setTimeout(() => {
      if (questionIndex < QUESTIONS.length - 1) {
        setStep(questionIndex + 2); // next question
      } else {
        setStep(QUESTIONS.length + 1); // contact step
      }
    }, 300);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email) { setError("Please enter your email address."); return; }
    setError("");
    setSubmitting(true);
    try {
      await submitLead.mutateAsync({
        name: name || "GEO Assessment",
        email,
        company: JSON.stringify({ website, scores: answers, totalScore, band: overall.band }),
        sourceSlug: "geo-readiness-assessment",
      });
      trackFormSubmit({ form_name: "GEO Readiness Assessment", form_location: "blog_geo_methodology" });
      setSubmitted(true);
      setStep(QUESTIONS.length + 2); // results
      // Notify parent so GEOShareToolbar can include personalised data in PDF
      if (onComplete) {
        const finalResults: AssessmentResult[] = QUESTIONS.map((q, i) => {
          const score = answers[i] >= 0 ? answers[i] : 0;
          const { label, advice } = getScoreLabel(score);
          return { metric: q.metric, metricLabel: q.metricLabel, accentColor: q.accentColor, score, label, advice };
        });
        const finalTotal = finalResults.reduce((sum, r) => sum + r.score, 0);
        const finalOverall = getOverallBand(finalTotal);
        onComplete({
          results: finalResults,
          totalScore: finalTotal,
          band: finalOverall.band,
          bandColor: finalOverall.color,
          bandDescription: finalOverall.description,
        });
      }
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  const progress = step === 0 ? 0 : step >= QUESTIONS.length + 2 ? 100 : Math.round((step / (QUESTIONS.length + 1)) * 100);

  const shareText = `I just scored ${totalScore}/18 on the GEO Readiness Assessment (${overall.band}). How does your brand measure up across the six GEO metrics?`;
  const shareUrl = "https://integrated.social/blog/geo-measurement-methodology-six-metrics-2026/";

  return (
    <section className="mt-16 border-t border-border pt-12" id="geo-readiness-assessment">
      <div className="mb-8">
        <span className="inline-block mb-3 px-3 py-1 rounded-full text-xs font-bold tracking-widest bg-[#00D4AA]/15 text-[#00D4AA] uppercase">
          Free Assessment
        </span>
        <h2 className="font-serif text-2xl md:text-3xl text-foreground mb-2">
          GEO Readiness Assessment
        </h2>
        <p className="text-muted-foreground text-sm max-w-2xl">
          Score your organisation across all six GEO metrics from the methodology above. Takes 3 minutes. Receive a personalised readiness report.
        </p>
      </div>

      <div className="rounded-2xl border border-border bg-muted/20 overflow-hidden">
        {/* Progress bar */}
        {step > 0 && step < QUESTIONS.length + 2 && (
          <div className="h-1.5 bg-muted">
            <div
              className="h-full bg-[#00D4AA] transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}

        <div className="p-6 md:p-10">

          {/* ── Intro ── */}
          {step === 0 && (
            <div className="text-center max-w-xl mx-auto">
              <div className="w-16 h-16 rounded-2xl bg-[#00D4AA]/15 flex items-center justify-center mx-auto mb-6">
                <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#00D4AA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
              </div>
              <h3 className="font-serif text-xl md:text-2xl text-foreground mb-3">
                How ready is your brand for AI-driven search?
              </h3>
              <p className="text-muted-foreground text-sm leading-relaxed mb-8">
                This 6-question assessment scores your organisation across the six GEO metrics defined in the methodology above: Discoverability, Mention Probability, Citation Probability, Narrative Accuracy, Recommendation Rate, and Commercial Influence. You will receive a scored readiness report with prioritised recommendations.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-muted-foreground mb-8">
                <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-[#00D4AA]" /> 3 minutes</span>
                <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-[#00D4AA]" /> 6 questions</span>
                <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-[#00D4AA]" /> Personalised report</span>
                <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-[#00D4AA]" /> No credit card</span>
              </div>
              <button
                onClick={handleStart}
                className="inline-flex items-center gap-2 bg-[#00D4AA] text-black font-semibold px-8 py-3.5 rounded-xl hover:bg-[#00bfa0] transition-colors shadow-lg shadow-[#00D4AA]/20"
              >
                Start the assessment <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* ── Question steps ── */}
          {step >= 1 && step <= QUESTIONS.length && (() => {
            const qIndex = step - 1;
            const q = QUESTIONS[qIndex];
            return (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <span
                      className="inline-block px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider"
                      style={{ background: `${q.accentColor}20`, color: q.accentColor }}
                    >
                      {q.metricLabel}
                    </span>
                    <span className="text-xs text-muted-foreground">Question {step} of {QUESTIONS.length}</span>
                  </div>
                  {step > 1 && (
                    <button
                      onClick={() => setStep(step - 1)}
                      className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <ArrowLeft className="w-3 h-3" /> Back
                    </button>
                  )}
                </div>

                <h3 className="font-serif text-xl md:text-2xl text-foreground mb-2">{q.question}</h3>
                <p className="text-sm text-muted-foreground mb-8 leading-relaxed">{q.subtext}</p>

                <div className="grid gap-3">
                  {q.options.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => handleAnswer(qIndex, opt.value)}
                      className={`w-full text-left p-4 rounded-xl border transition-all duration-150 group ${
                        answers[qIndex] === opt.value
                          ? "border-[#00D4AA] bg-[#00D4AA]/10"
                          : "border-border hover:border-[#00D4AA]/50 hover:bg-muted/50"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <span
                          className={`mt-0.5 shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                            answers[qIndex] === opt.value ? "border-[#00D4AA] bg-[#00D4AA]" : "border-border"
                          }`}
                        >
                          {answers[qIndex] === opt.value && (
                            <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                          )}
                        </span>
                        <div>
                          <p className="font-semibold text-sm text-foreground mb-0.5">{opt.label}</p>
                          <p className="text-xs text-muted-foreground leading-relaxed">{opt.description}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* ── Contact step ── */}
          {step === QUESTIONS.length + 1 && (
            <div>
              <div className="flex items-center gap-2 mb-6">
                <span className="inline-block px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-[#00D4AA]/20 text-[#00D4AA]">
                  Almost done
                </span>
                <button
                  onClick={() => setStep(QUESTIONS.length)}
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors ml-auto"
                >
                  <ArrowLeft className="w-3 h-3" /> Back
                </button>
              </div>

              <h3 className="font-serif text-xl md:text-2xl text-foreground mb-2">
                Where should we send your GEO Readiness Report?
              </h3>
              <p className="text-sm text-muted-foreground mb-8 leading-relaxed">
                Your personalised report will show your score across all six metrics, where you rank against the evidence benchmarks, and the three highest-priority actions for your programme.
              </p>

              {/* Score preview */}
              <div className="rounded-xl border border-border bg-muted/30 p-4 mb-8">
                <p className="text-xs text-muted-foreground mb-3 font-medium uppercase tracking-wider">Your preliminary scores</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {results.map((r) => (
                    <div key={r.metric} className="flex items-center gap-2">
                      <span
                        className="shrink-0 w-2 h-2 rounded-full"
                        style={{ background: r.accentColor }}
                      />
                      <span className="text-xs text-muted-foreground truncate">{r.metricLabel}</span>
                      <span className="ml-auto text-xs font-semibold text-foreground">{r.score}/3</span>
                    </div>
                  ))}
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Your name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="First name"
                    className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground placeholder:text-muted-foreground text-sm focus:outline-none focus:ring-2 focus:ring-[#00D4AA]/50 focus:border-[#00D4AA] transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Work email <span className="text-[#DC2626]">*</span></label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    required
                    className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground placeholder:text-muted-foreground text-sm focus:outline-none focus:ring-2 focus:ring-[#00D4AA]/50 focus:border-[#00D4AA] transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Website URL</label>
                  <input
                    type="url"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    placeholder="https://yourcompany.com"
                    className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground placeholder:text-muted-foreground text-sm focus:outline-none focus:ring-2 focus:ring-[#00D4AA]/50 focus:border-[#00D4AA] transition-colors"
                  />
                </div>
                {error && <p className="text-sm text-[#DC2626]">{error}</p>}
                <button
                  type="submit"
                  disabled={submitting || !email}
                  className="w-full flex items-center justify-center gap-2 bg-[#00D4AA] text-black font-semibold px-6 py-3.5 rounded-xl hover:bg-[#00bfa0] disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-lg shadow-[#00D4AA]/20"
                >
                  {submitting ? "Sending…" : <>Get my GEO Readiness Report <ArrowRight className="w-4 h-4" /></>}
                </button>
                <p className="text-xs text-muted-foreground text-center">
                  No spam. No lock-in. Unsubscribe any time. <a href="/privacy-policy/" className="underline hover:text-foreground">Privacy Policy</a>.
                </p>
              </form>
            </div>
          )}

          {/* ── Results ── */}
          {step === QUESTIONS.length + 2 && (
            <div>
              <div className="text-center mb-10">
                <div
                  className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-4"
                  style={{ background: `${overall.color}20` }}
                >
                  <span className="font-serif text-3xl font-bold" style={{ color: overall.color }}>{totalScore}</span>
                </div>
                <p className="text-xs text-muted-foreground mb-1 uppercase tracking-widest">Your GEO Readiness Score</p>
                <h3 className="font-serif text-2xl md:text-3xl text-foreground mb-2" style={{ color: overall.color }}>
                  {overall.band}
                </h3>
                <p className="text-sm text-muted-foreground max-w-lg mx-auto leading-relaxed">{overall.description}</p>
                <p className="text-xs text-muted-foreground mt-2">{totalScore} / 18 points</p>
              </div>

              {/* Metric breakdown */}
              <div className="space-y-4 mb-10">
                <h4 className="font-semibold text-sm text-foreground uppercase tracking-wider">Metric breakdown</h4>
                {results.map((r) => (
                  <div key={r.metric} className="rounded-xl border border-border p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span
                          className="w-2.5 h-2.5 rounded-full shrink-0"
                          style={{ background: r.accentColor }}
                        />
                        <span className="font-semibold text-sm text-foreground">{r.metricLabel}</span>
                      </div>
                      <span
                        className="text-xs font-bold px-2 py-0.5 rounded-full"
                        style={{ background: `${r.accentColor}20`, color: r.accentColor }}
                      >
                        {r.label}
                      </span>
                    </div>
                    {/* Score bar */}
                    <div className="h-1.5 bg-muted rounded-full mb-2">
                      <div
                        className="h-full rounded-full transition-all duration-700"
                        style={{ width: `${(r.score / 3) * 100}%`, background: r.accentColor }}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{r.advice}</p>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <div className="rounded-2xl bg-foreground text-background p-6 md:p-8 mb-6">
                <h4 className="font-serif text-lg md:text-xl mb-2">Want a full GEO audit?</h4>
                <p className="text-background/70 text-sm mb-5 leading-relaxed">
                  The assessment above is self-reported. A full Integrated.Social GEO audit runs your domain against 30+ queries across ChatGPT, Gemini, Perplexity, and Google AI Mode - with scored results across all six metrics and a prioritised action plan.
                </p>
                <div className="flex flex-wrap gap-3">
                  <a
                    href="https://calendly.com/modielnadi/30min"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-[#00D4AA] text-black font-semibold px-5 py-2.5 rounded-lg hover:bg-[#00bfa0] transition-colors text-sm"
                  >
                    Book a free GEO strategy call <ArrowRight className="w-3.5 h-3.5" />
                  </a>
                </div>
              </div>

              {/* Share results */}
              <div className="rounded-xl border border-border p-5">
                <p className="text-sm font-medium text-foreground mb-3">Share your score</p>
                <div className="flex flex-wrap gap-2">
                  <a
                    href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}&summary=${encodeURIComponent(shareText)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#0A66C2] text-white text-xs font-semibold hover:bg-[#0958a8] transition-colors"
                  >
                    <Linkedin className="w-3.5 h-3.5" /> Share on LinkedIn
                  </a>
                  <a
                    href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-black text-white text-xs font-semibold hover:bg-zinc-800 transition-colors"
                  >
                    <Twitter className="w-3.5 h-3.5" /> Share on X
                  </a>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-border text-foreground text-xs font-semibold hover:bg-muted transition-colors"
                  >
                    <Share2 className="w-3.5 h-3.5" /> Copy result
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </section>
  );
}
